import {
  BillingUiState,
  BillingUiStateKind,
  StripeSubscriptionStatus,
  Team,
} from '@this+that/models';

const PAST_DUE_STATUSES: ReadonlySet<StripeSubscriptionStatus> = new Set([
  StripeSubscriptionStatus.PAST_DUE,
  StripeSubscriptionStatus.UNPAID,
]);

// CANCELED / INCOMPLETE / INCOMPLETE_EXPIRED never had (or lost) a chargeable
// subscription — the tab renders exactly like a team that never subscribed.
const STARTER_STATUSES: ReadonlySet<StripeSubscriptionStatus> = new Set([
  StripeSubscriptionStatus.CANCELED,
  StripeSubscriptionStatus.INCOMPLETE,
  StripeSubscriptionStatus.INCOMPLETE_EXPIRED,
]);

type BillingStateTeam = Pick<
  Team,
  'subscriptionStatus' | 'cancelAtPeriodEnd' | 'trialEndsAt'
>;

function getStarterUiState(
  trialEndsAt: number | undefined,
  now: number,
): BillingUiState {
  if (trialEndsAt === undefined) {
    return { kind: BillingUiStateKind.STARTER, cancelAtPeriodEnd: false };
  }
  return {
    kind:
      trialEndsAt > now
        ? BillingUiStateKind.APP_TRIAL
        : BillingUiStateKind.APP_TRIAL_EXPIRED,
    cancelAtPeriodEnd: false,
  };
}

export function getBillingUiState(
  team: BillingStateTeam,
  now: number = Date.now(),
): BillingUiState {
  const status = team.subscriptionStatus;
  const cancelAtPeriodEnd = Boolean(team.cancelAtPeriodEnd);

  if (!status || STARTER_STATUSES.has(status)) {
    return getStarterUiState(team.trialEndsAt, now);
  }

  if (PAST_DUE_STATUSES.has(status)) {
    return { kind: BillingUiStateKind.PAST_DUE, cancelAtPeriodEnd };
  }

  if (status === StripeSubscriptionStatus.TRIALING) {
    return { kind: BillingUiStateKind.TRIALING, cancelAtPeriodEnd };
  }

  if (status === StripeSubscriptionStatus.ACTIVE) {
    return { kind: BillingUiStateKind.ACTIVE, cancelAtPeriodEnd };
  }

  // PAUSED, or any future Stripe status this UI doesn't model yet — treat as
  // Starter rather than showing a broken "active" plan card.
  return getStarterUiState(team.trialEndsAt, now);
}
